import { formatDuration, intervalToDuration } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { MaterialIcons } from '@/common/components/shared/material-icons/MaterialIcons';
import {
  type ExportHistoryFieldCreatedByFragment,
  ExportImportStatus,
  type ImportHistoryFieldCreatedByFragment,
} from '@/common/graphql';
import { DateUtils } from '@/common/utils';
import { Cell } from './Cell';

type HistoryFieldCreatedBy = (ExportHistoryFieldCreatedByFragment | ImportHistoryFieldCreatedByFragment)['createdBy'];

interface CellValueRequestProps {
  status: ExportImportStatus;
  createdAt: string;
  createdBy?: HistoryFieldCreatedBy;
  updatedAt: string;
  onlyMyTask: boolean;
}

export const CellValueRequest = ({ status, createdAt, updatedAt, createdBy, onlyMyTask }: CellValueRequestProps) => (
  <Cell className="pl-4 flex-col items-start justify-center gap-y-0.5 text-2xs">
    <div className="flex items-center gap-1">
      <MaterialIcons iconName="nest_clock_farsight_analog" />
      <div className="flex-1 line-clamp-1 tabular-nums">{DateUtils.formatDateTime(createdAt)}</div>
    </div>
    <div className="flex items-center gap-1 text-gray-7">
      <MaterialIcons iconName={statusInProgress(status) ? 'hourglass' : 'timer'} />
      <div className="flex-1 line-clamp-1 tabular-nums">
        <ElapsedTime status={status} createdAt={createdAt} updatedAt={updatedAt} />
      </div>
    </div>
    {!onlyMyTask && createdBy && (
      <div className="flex items-center gap-1 text-gray-7" title={createdBy.generalInformation.preferredFullName}>
        <MaterialIcons iconName="person" />
        <div className="flex-1 line-clamp-1">{createdBy.generalInformation.preferredFullName}</div>
      </div>
    )}
  </Cell>
);

const statusInProgress = (status: ExportImportStatus) =>
  status === ExportImportStatus.Accepted || status === ExportImportStatus.InProgress;

const ElapsedTime = ({
  status,
  createdAt,
  updatedAt,
}: Pick<CellValueRequestProps, 'status' | 'createdAt' | 'updatedAt'>) => {
  let duration = { seconds: 0 } as Duration;

  const start = DateUtils.parseDate(createdAt);
  const end = [ExportImportStatus.Accepted, ExportImportStatus.InProgress].includes(status)
    ? new Date()
    : DateUtils.parseDate(updatedAt);
  if (start && end && start !== end) {
    duration = intervalToDuration({ start, end });
  }

  return (
    <>
      {formatDuration(duration, { format: ['hours', 'minutes', 'seconds'], zero: true, locale: enUS })
        .replace(/ hours?/, 'h')
        .replace(/ minutes?/, 'm')
        .replace(/ seconds?/, 's')
        .replace('0h ', '')
        .replace('0m ', '')}
    </>
  );
};
